import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import * as notificationService from '~/services/notifications/notificationService';
import * as groupService from '~/services/group/groupService';
import type { Notification } from '~/services/notifications/notificationService';

interface NotificationCenterProps {
  onClose: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ onClose: _onClose }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null); // 正在处理的通知ID

  // 加载通知列表
  useEffect(() => {
    if (user) {
      loadNotifications();
      loadUnreadCount();
      
      // 订阅实时通知
      const unsubscribe = notificationService.subscribeToNotifications((notification) => {
        setNotifications((prev) => [notification, ...prev]);
        setUnreadCount((prev) => prev + 1);
      });

      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [user]);

  const loadNotifications = async () => {
    setLoading(true);
    const { notifications: notificationList, error } = await notificationService.getUserNotifications(false);
    if (error) {
      console.error('[NotificationCenter] 加载通知失败:', error);
    } else {
      setNotifications(notificationList);
    }
    setLoading(false);
  };

  const loadUnreadCount = async () => {
    const { count, error } = await notificationService.getUnreadNotificationCount();
    if (!error) {
      setUnreadCount(count);
    }
  };

  // 检查邀请是否过期
  const isInvitationExpired = (notification: Notification): boolean => {
    if (notification.type !== 'group_invite' || !notification.data?.expires_at) {
      return false;
    }
    return new Date(notification.data.expires_at) < new Date();
  };

  // 处理分组邀请通知
  const handleGroupInvite = async (notification: Notification, action: 'accept' | 'reject') => {
    if (!notification.data?.invitation_id) {
      console.error('[NotificationCenter] 邀请通知缺少 invitation_id');
      return;
    }

    // 检查是否过期
    if (isInvitationExpired(notification)) {
      alert('该邀请已过期');
      // 标记通知为已读
      await notificationService.markNotificationAsRead(notification.id);
      await loadNotifications();
      await loadUnreadCount();
      return;
    }

    setProcessing(notification.id);

    try {
      if (action === 'accept') {
        const { error } = await groupService.acceptInvitation(notification.data.invitation_id);
        if (error) {
          alert(`接受邀请失败: ${error.message}`);
        } else {
          // 标记通知为已读
          await notificationService.markNotificationAsRead(notification.id);
          // 重新加载通知列表
          await loadNotifications();
          await loadUnreadCount();
          alert('已成功加入分组！');
        }
      } else {
        const { error } = await groupService.rejectInvitation(notification.data.invitation_id);
        if (error) {
          alert(`拒绝邀请失败: ${error.message}`);
        } else {
          // 标记通知为已读
          await notificationService.markNotificationAsRead(notification.id);
          // 重新加载通知列表
          await loadNotifications();
          await loadUnreadCount();
        }
      }
    } catch (error: any) {
      console.error('[NotificationCenter] 处理邀请失败:', error);
      alert(`操作失败: ${error.message || '未知错误'}`);
    } finally {
      setProcessing(null);
    }
  };

  // 标记通知为已读
  const handleMarkAsRead = async (notificationId: string) => {
    await notificationService.markNotificationAsRead(notificationId);
    await loadNotifications();
    await loadUnreadCount();
  };

  // 标记所有通知为已读
  const handleMarkAllAsRead = async () => {
    await notificationService.markAllNotificationsAsRead();
    await loadNotifications();
    await loadUnreadCount();
  };

  // 删除通知
  const handleDelete = async (notificationId: string) => {
    if (confirm('确定要删除这条通知吗？')) {
      await notificationService.deleteNotification(notificationId);
      await loadNotifications();
      await loadUnreadCount();
    }
  };

  // 获取通知图标
  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'group_invite':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
      case 'group_invite_accepted':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'todo_assigned':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        );
    }
  };

  // 格式化时间
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return '刚刚';
    if (minutes < 60) return `${minutes}分钟前`;
    if (hours < 24) return `${hours}小时前`;
    if (days < 7) return `${days}天前`;
    return date.toLocaleDateString('zh-CN');
  };

  if (!user) {
    return (
      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
        请先登录以查看通知
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* 头部操作栏 */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        {notifications.length > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
          >
            全部已读
          </button>
        )}
      </div>

      {/* 通知列表 */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">加载中...</div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <p className="text-gray-500 dark:text-gray-400">暂无通知</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {notifications.map((notification) => {
              const isProcessing = processing === notification.id;
              const isGroupInvite = notification.type === 'group_invite';
              const hasInvitationId = !!notification.data?.invitation_id;

              return (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                    !notification.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* 图标 */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                      notification.type === 'group_invite' 
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        : notification.type === 'group_invite_accepted'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                      {getNotificationIcon(notification.type)}
                    </div>

                    {/* 内容 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <h4 className={`text-sm font-medium mb-1 ${
                            !notification.read 
                              ? 'text-gray-900 dark:text-gray-100' 
                              : 'text-gray-700 dark:text-gray-300'
                          }`}>
                            {notification.title}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                            <span>{formatTime(notification.created_at)}</span>
                            {!notification.read && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            )}
                          </div>
                        </div>

                        {/* 操作按钮 */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {isGroupInvite && hasInvitationId && !notification.read && (
                            <>
                              {isInvitationExpired(notification) ? (
                                <span className="px-3 py-1.5 text-sm bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400 rounded-lg">
                                  已过期
                                </span>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleGroupInvite(notification, 'accept')}
                                    disabled={isProcessing}
                                    className="px-3 py-1.5 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    {isProcessing ? '处理中...' : '接受'}
                                  </button>
                                  <button
                                    onClick={() => handleGroupInvite(notification, 'reject')}
                                    disabled={isProcessing}
                                    className="px-3 py-1.5 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    {isProcessing ? '处理中...' : '拒绝'}
                                  </button>
                                </>
                              )}
                            </>
                          )}
                          {!notification.read && (
                            <button
                              onClick={() => handleMarkAsRead(notification.id)}
                              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                              title="标记为已读"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(notification.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            title="删除"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;
